import React from "react";
import type {ReactNode} from "react";
import type {AnyExtension} from "@tiptap/core";
import type {Node as ProseMirrorNode, Schema} from "@tiptap/pm/model";
import {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertPageBreakOutlinedIcon from "@mui/icons-material/InsertPageBreakOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import {
    PrintoutContainerColumnNode,
    PrintoutContainerNode,
} from "./container/PrintoutContainerNodes";
import {PrintoutDataListNode} from "./dataList/PrintoutDataListNode";
import {PrintoutDividerNode} from "./divider/PrintoutDividerNode";
import {PrintoutFieldNode} from "./field/PrintoutFieldNode";
import {PrintoutWritingAreaNode} from "./writingArea/PrintoutWritingAreaNode";
import {PrintoutFolioTableNode} from "./folioTable/PrintoutFolioTableNode";
import {PrintoutImageNode} from "./image/PrintoutImageNode";
import {PrintoutPageBreakNode} from "./pageBreak/PrintoutPageBreakNode";
import {PrintoutQrCodeNode} from "./qrCode/PrintoutQrCodeNode";
import {printoutTableExtensions} from "./table/printoutTableExtensions";
import {createPrintoutTableNode} from "./table/printoutTableCommands";
import {
    createPrintoutContainerColumnsJson,
    DEFAULT_PRINTOUT_CONTAINER_COLUMNS,
} from "./container/printoutContainerOptions";
import {
    createDataListAttrs,
    getDataListAttrs,
    getDataListSourceDefinition,
} from "./dataList/printoutDataListOptions";
import {getFieldAttrs} from "./field/printoutFieldOptions";
import {getWritingAreaAttrs} from "./writingArea/printoutWritingAreaOptions";
import {getFolioTableAttrs} from "./folioTable/printoutFolioTableOptions";

export interface PrintoutBlockDescriptor {
    id: string;
    type: string;
    label: string;
    description: string;
    icon: ReactNode;
    extensions: readonly AnyExtension[];
    createNode: (schema: Schema) => ProseMirrorNode | null;
    // Tables start editing in their first cell; other blocks leave a caret after the new block.
    selectContentOnInsert?: boolean;
    hiddenFromInsertMenu?: boolean;
    types?: readonly PrintoutType[];
}

export const PRINTOUT_BLOCK_REGISTRY = [
    {
        id: "printoutDivider",
        type: "printoutDivider",
        label: "Divider",
        description: "Separates sections with a horizontal rule and an optional label.",
        icon: <RemoveIcon fontSize="small"/>,
        extensions: [PrintoutDividerNode],
        hiddenFromInsertMenu: false,
        createNode: (schema: Schema) => schema.nodes.printoutDivider.create(),
    },
    {
        id: "printoutPageBreak",
        type: "printoutPageBreak",
        label: "Page break",
        description: "Ends the page here. Content that follows starts at the top of the next page.",
        icon: <InsertPageBreakOutlinedIcon fontSize="small"/>,
        extensions: [PrintoutPageBreakNode],
        hiddenFromInsertMenu: false,
        createNode: (schema: Schema) => schema.nodes.printoutPageBreak.create(),
    },
    {
        id: "printoutImage",
        type: "printoutImage",
        label: "Image",
        description: "Adds an uploaded image or the organization logo at a fixed print size.",
        icon: <ImageOutlinedIcon fontSize="small"/>,
        extensions: [PrintoutImageNode],
        hiddenFromInsertMenu: false,
        createNode: (schema: Schema) => schema.nodes.printoutImage.create(),
    },
    {
        id: "printoutQrCode",
        type: "printoutQrCode",
        label: "QR code",
        description: "Creates a scannable QR code for a web address.",
        icon: <QrCode2OutlinedIcon fontSize="small"/>,
        extensions: [PrintoutQrCodeNode],
        hiddenFromInsertMenu: false,
        createNode: (schema: Schema) => schema.nodes.printoutQrCode.create(),
    },
    {
        id: "printoutField",
        type: "printoutField",
        label: "Field",
        description: "Adds a compact line or box for one short handwritten value.",
        icon: <DrawOutlinedIcon fontSize="small"/>,
        extensions: [PrintoutFieldNode],
        hiddenFromInsertMenu: false,
        createNode: (schema: Schema) => schema.nodes.printoutField.create(getFieldAttrs()),
    },
    {
        id: "printoutWritingArea",
        type: "printoutWritingArea",
        label: "Writing area",
        description: "Adds a larger area for handwritten notes. It fills its current column.",
        icon: <EditNoteOutlinedIcon fontSize="small"/>,
        extensions: [PrintoutWritingAreaNode],
        hiddenFromInsertMenu: false,
        createNode: (schema: Schema) => schema.nodes.printoutWritingArea.create(getWritingAreaAttrs()),
    },
    {
        id: "printoutContainer",
        type: "printoutContainer",
        label: "Container",
        description: "Arranges blocks in side-by-side columns and controls the space above them.",
        icon: <ViewWeekOutlinedIcon fontSize="small"/>,
        extensions: [PrintoutContainerNode, PrintoutContainerColumnNode],
        hiddenFromInsertMenu: false,
        createNode: (schema: Schema) => schema.nodes.printoutContainer.create(
            null,
            createPrintoutContainerColumnsJson(DEFAULT_PRINTOUT_CONTAINER_COLUMNS)
                .map(column => schema.nodeFromJSON(column)),
        ),
    },
    // Each data source is an insert variant; register their shared node only once.
    {
        id: "printoutDataListFolioItems",
        type: "printoutDataList",
        label: "Folio items",
        description: getDataListSourceDefinition("folioItems").description,
        icon: <DatasetOutlinedIcon fontSize="small"/>,
        extensions: [PrintoutDataListNode],
        hiddenFromInsertMenu: false,
        types: [PrintoutType.FOLIO_CONFIRMATION],
        createNode: (schema: Schema) => schema.nodes.printoutDataList.create(
            getDataListAttrs(createDataListAttrs("folioItems")),
        ),
    },
    {
        id: "printoutDataListMealPeriods",
        type: "printoutDataList",
        label: "Meal plan reservations",
        description: getDataListSourceDefinition("reservationMealPeriods").description,
        icon: <DatasetOutlinedIcon fontSize="small"/>,
        extensions: [],
        hiddenFromInsertMenu: false,
        types: [PrintoutType.RESERVATION_CONFIRMATION],
        createNode: (schema: Schema) => schema.nodes.printoutDataList.create(
            getDataListAttrs(createDataListAttrs("reservationMealPeriods")),
        ),
    },
    {
        id: "printoutDataListGuestDietaryRequirements",
        type: "printoutDataList",
        label: "Guest dietary requirements",
        description: getDataListSourceDefinition("guestDietaryRequirements").description,
        icon: <DatasetOutlinedIcon fontSize="small"/>,
        extensions: [],
        hiddenFromInsertMenu: false,
        types: [PrintoutType.RESERVATION_CONFIRMATION],
        createNode: (schema: Schema) => schema.nodes.printoutDataList.create(
            getDataListAttrs(createDataListAttrs("guestDietaryRequirements")),
        ),
    },
    {
        id: "printoutDataListAgendaItems",
        type: "printoutDataList",
        label: "Agenda items",
        description: getDataListSourceDefinition("agendaItems").description,
        icon: <DatasetOutlinedIcon fontSize="small"/>,
        extensions: [],
        hiddenFromInsertMenu: false,
        types: [PrintoutType.AGENDA_HANDOUT],
        createNode: (schema: Schema) => schema.nodes.printoutDataList.create(
            getDataListAttrs(createDataListAttrs("agendaItems")),
        ),
    },
    {
        id: "printoutFolioTable",
        type: "printoutFolioTable",
        label: "Folio table",
        description: "Shows folio charges, taxes, payments and totals in a statement layout.",
        icon: <ReceiptLongOutlinedIcon fontSize="small"/>,
        extensions: [PrintoutFolioTableNode],
        hiddenFromInsertMenu: false,
        types: [PrintoutType.FOLIO_CONFIRMATION],
        createNode: (schema: Schema) => schema.nodes.printoutFolioTable.create(getFolioTableAttrs()),
    },
    {
        id: "table",
        type: "table",
        // `Table` is already the restaurant-table translation key.
        label: "Table block",
        description: "Adds an editable table for manually arranged text, variables and supported blocks.",
        icon: <TableChartOutlinedIcon fontSize="small"/>,
        extensions: printoutTableExtensions,
        hiddenFromInsertMenu: false,
        createNode: (schema: Schema) => createPrintoutTableNode(schema, 3, 3),
        selectContentOnInsert: true,
    },
] as const satisfies readonly PrintoutBlockDescriptor[];

export type PrintoutBlockType = typeof PRINTOUT_BLOCK_REGISTRY[number]["type"];

export const PRINTOUT_BLOCK_EXTENSIONS = PRINTOUT_BLOCK_REGISTRY.flatMap((block) => [...block.extensions]);

// Widened once, so `types` reads as the optional field it is rather than as a per-entry literal.
const PRINTOUT_BLOCK_DESCRIPTORS: readonly PrintoutBlockDescriptor[] = PRINTOUT_BLOCK_REGISTRY;

export const getPrintoutInsertBlocks = (
    type?: PrintoutType | null,
): readonly PrintoutBlockDescriptor[] => PRINTOUT_BLOCK_DESCRIPTORS.filter((block) => (
    !block.hiddenFromInsertMenu && (!type || !block.types || block.types.includes(type))
));

export const findPrintoutBlockById = (id: string): PrintoutBlockDescriptor | undefined => (
    PRINTOUT_BLOCK_DESCRIPTORS.find((block) => block.id === id)
);
